import React, { useMemo } from 'react';

/**
 * Picker for a ReportingEvent's schedule. Renders different inner controls
 * depending on the selected ScheduleType (Daily/Weekly/Monthly/Quarterly/
 * OneTime). The schedule config is round-tripped to the backend as a JSON
 * blob; this component just owns the structured shape.
 *
 * Value:
 *   { scheduleType: 'Weekly',
 *     scheduleConfig: { dayOfWeek: 1, hour: 7, minute: 0 },
 *     timeZone: 'America/Chicago' }
 */

const SCHEDULE_TYPES = [
  { id: 'OneTime',   label: 'One time' },
  { id: 'Daily',     label: 'Daily' },
  { id: 'Weekly',    label: 'Weekly' },
  { id: 'Monthly',   label: 'Monthly' },
  { id: 'Quarterly', label: 'Quarterly' },
];

const TIMEZONES = [
  { id: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { id: 'America/Denver',      label: 'Mountain (Denver)' },
  { id: 'America/Phoenix',     label: 'Arizona (Phoenix)' },
  { id: 'America/Chicago',     label: 'Central (Chicago)' },
  { id: 'America/New_York',    label: 'Eastern (New York)' },
  { id: 'UTC',                 label: 'UTC' },
];

const DAYS_OF_WEEK = [
  { id: 0, label: 'Sunday' },
  { id: 1, label: 'Monday' },
  { id: 2, label: 'Tuesday' },
  { id: 3, label: 'Wednesday' },
  { id: 4, label: 'Thursday' },
  { id: 5, label: 'Friday' },
  { id: 6, label: 'Saturday' },
];

function defaultConfig(type) {
  switch (type) {
    case 'Daily':     return { hour: 7, minute: 0 };
    case 'Weekly':    return { dayOfWeek: 1, hour: 7, minute: 0 };
    case 'Monthly':   return { dayOfMonth: 1, hour: 7, minute: 0 };
    case 'Quarterly': return { weekOfQuarter: 1, dayOfWeek: 1, hour: 7, minute: 0 };
    case 'OneTime':   return { runAt: new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 16) };
    default:          return {};
  }
}

function HourMinute({ value, onChange }) {
  const hour = value?.hour ?? 7;
  const minute = value?.minute ?? 0;
  return (
    <span className="sched-hm">
      <select
        value={hour}
        onChange={(e) => onChange({ ...value, hour: Number(e.target.value) })}
      >
        {Array.from({ length: 24 }, (_, h) => (
          <option key={h} value={h}>{String(h).padStart(2, '0')}</option>
        ))}
      </select>
      <span className="sched-colon">:</span>
      <select
        value={minute}
        onChange={(e) => onChange({ ...value, minute: Number(e.target.value) })}
      >
        {[0, 15, 30, 45].map(m => (
          <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
        ))}
      </select>
    </span>
  );
}

function SchedulePicker({ scheduleType, scheduleConfig, timeZone, onChange, nextRunAt }) {
  const config = useMemo(() => scheduleConfig || defaultConfig(scheduleType || 'Weekly'), [scheduleConfig, scheduleType]);
  const tz = timeZone || 'America/Chicago';

  const setType = (t) => onChange({ scheduleType: t, scheduleConfig: defaultConfig(t), timeZone: tz });
  const setConfig = (cfg) => onChange({ scheduleType, scheduleConfig: cfg, timeZone: tz });
  const setTz = (z) => onChange({ scheduleType, scheduleConfig: config, timeZone: z });

  return (
    <div className="sched-root">
      <div className="sched-row">
        <label>Frequency</label>
        <select value={scheduleType || 'Weekly'} onChange={(e) => setType(e.target.value)}>
          {SCHEDULE_TYPES.map(t => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
      </div>

      {scheduleType === 'Daily' && (
        <div className="sched-row">
          <label>At</label>
          <HourMinute value={config} onChange={setConfig} />
        </div>
      )}

      {scheduleType === 'Weekly' && (
        <div className="sched-row">
          <label>Every</label>
          <select
            value={config.dayOfWeek ?? 1}
            onChange={(e) => setConfig({ ...config, dayOfWeek: Number(e.target.value) })}
          >
            {DAYS_OF_WEEK.map(d => (
              <option key={d.id} value={d.id}>{d.label}</option>
            ))}
          </select>
          <span className="sched-at">at</span>
          <HourMinute value={config} onChange={setConfig} />
        </div>
      )}

      {scheduleType === 'Monthly' && (
        <div className="sched-row">
          <label>On the</label>
          <select
            value={config.dayOfMonth ?? 1}
            onChange={(e) => setConfig({ ...config, dayOfMonth: Number(e.target.value) })}
          >
            {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <span className="sched-at">of each month at</span>
          <HourMinute value={config} onChange={setConfig} />
        </div>
      )}

      {scheduleType === 'Quarterly' && (
        <div className="sched-row sched-row-wrap">
          <label>Week of quarter</label>
          <select
            value={config.weekOfQuarter ?? 1}
            onChange={(e) => setConfig({ ...config, weekOfQuarter: Number(e.target.value) })}
          >
            {Array.from({ length: 13 }, (_, i) => i + 1).map(w => (
              <option key={w} value={w}>{w}</option>
            ))}
          </select>
          <span className="sched-at">on</span>
          <select
            value={config.dayOfWeek ?? 1}
            onChange={(e) => setConfig({ ...config, dayOfWeek: Number(e.target.value) })}
          >
            {DAYS_OF_WEEK.map(d => (
              <option key={d.id} value={d.id}>{d.label}</option>
            ))}
          </select>
          <span className="sched-at">at</span>
          <HourMinute value={config} onChange={setConfig} />
        </div>
      )}

      {scheduleType === 'OneTime' && (
        <div className="sched-row">
          <label>Run at</label>
          <input
            type="datetime-local"
            value={config.runAt || ''}
            onChange={(e) => setConfig({ ...config, runAt: e.target.value })}
          />
        </div>
      )}

      <div className="sched-row">
        <label>Time zone</label>
        <select value={tz} onChange={(e) => setTz(e.target.value)}>
          {TIMEZONES.map(z => (
            <option key={z.id} value={z.id}>{z.label}</option>
          ))}
        </select>
      </div>

      {nextRunAt && (
        <div className="sched-next">
          Next run: <strong>{new Date(nextRunAt).toLocaleString()}</strong>
        </div>
      )}
    </div>
  );
}

export default SchedulePicker;
